<template>
  <el-dialog
    :model-value="modelValue"
    title="Alta rápida de producto"
    width="600px"
    :close-on-click-modal="false"
    :show-close="!isSaving"
    append-to-body
    @update:model-value="$emit('update:modelValue', $event)"
    @open="onOpen"
  >
    <el-form
      ref="formRef"
      v-loading="isSaving"
      :model="form"
      :rules="rules"
      label-position="top"
      @submit.prevent
    >
      <el-form-item
        label="Descripción del nuevo producto"
        prop="descripcion"
      >
        <el-input
          v-model="form.descripcion"
          placeholder="Descripción comercial..."
        />
      </el-form-item>
      <el-form-item
        label="Unidad de medida"
        prop="codigoUnidadMedida"
      >
        <el-select
          v-model="form.codigoUnidadMedida"
          placeholder="Unidad"
          style="width: 100%"
        >
          <el-option
            v-for="um in unidadesMedida"
            :key="um.Codigo"
            :label="um.Descripcion"
            :value="um.Codigo"
          />
        </el-select>
      </el-form-item>
      <el-row :gutter="15">
        <el-col :span="12">
          <el-form-item label="Código equivalente">
            <el-input
              v-model="form.codigoEquivalente"
              placeholder="Opcional..."
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="Código adicional">
            <el-input
              v-model="form.codigoAdicional"
              placeholder="Opcional..."
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <template #footer>
      <el-button
        :disabled="isSaving"
        @click="$emit('update:modelValue', false)"
      >
        Cancelar
      </el-button>
      <el-button
        type="primary"
        :disabled="isSaving"
        @click="onConfirm"
      >
        Crear producto
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  reactive,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElNotification } from 'element-plus';
import { useUnidadMedidaStore } from '@/stores/unidad_medida/unidad_medida_store';
import { useMaterialStore } from '@/stores/materiales_store';
import { buildMaterialRapidoPayload } from '@/shared/utils/material_rapido.js';
import { mensajeErrorApi } from '@/shared/utils/error_api.js';

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  clientes: { type: Array, default: () => [] },
  codigoUnidadMedidaDefault: { type: Number, default: null },
  descripcionDefault: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'created']);

const unidadMedidaStore = useUnidadMedidaStore();
const materialStore = useMaterialStore();
const { unidadesMedida } = storeToRefs(unidadMedidaStore);

const formRef = ref(null);
const isSaving = ref(false);
const form = reactive({
  descripcion: '',
  codigoUnidadMedida: null,
  codigoEquivalente: '',
  codigoAdicional: '',
});

const rules = {
  descripcion: [{ required: true, message: 'Ingresar descripción', trigger: 'blur' }],
  codigoUnidadMedida: [
    { required: true, message: 'Seleccionar unidad de medida', trigger: 'change' },
  ],
};

const onOpen = async () => {
  form.descripcion = props.descripcionDefault || '';
  form.codigoUnidadMedida = props.codigoUnidadMedidaDefault;
  form.codigoEquivalente = '';
  form.codigoAdicional = '';
  formRef.value?.clearValidate();

  if (unidadesMedida.value.length === 0) {
    await unidadMedidaStore.findAllUnidadMedidaHTTP({ page: 0, Activo: true });
  }
};

const onConfirm = async () => {
  const valid = await formRef.value?.validate().catch(() => false);
  if (!valid) return;

  isSaving.value = true;
  try {
    const response = await materialStore.createMaterialRapidoHTTP(
      buildMaterialRapidoPayload({
        descripcion: form.descripcion,
        codigoUnidadMedida: form.codigoUnidadMedida,
        codigoEquivalente: form.codigoEquivalente,
        codigoAdicional: form.codigoAdicional,
        clientes: props.clientes,
      }),
    );
    const codigo = response?.Codigo || response?.data?.Codigo;
    if (!codigo) throw new Error('El servidor no devolvió el código del producto.');

    emit('created', { Codigo: codigo, Descripcion: form.descripcion });
    emit('update:modelValue', false);
  } catch (error) {
    ElNotification({
      title: 'Error',
      message: mensajeErrorApi(error, 'No se pudo crear el producto.'),
      type: 'error',
    });
  } finally {
    isSaving.value = false;
  }
};
</script>
